import * as React from "react";
import { JobTypeTag, jobSerial } from "../jobType";
import { useState, useEffect } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { setlogo } from "./Nav/navSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useNavigate } from "react-router-dom";
import { useGetQEJobCardHistoryQuery } from "../api/apiSlice";
import { BG, CARD_BG, BORDER } from "../../Global/colors";

export default function History() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  dispatch(setlogo(false));

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isSuccess } = useGetQEJobCardHistoryQuery(debouncedSearch);

  if (!isSuccess) return null;

  const filtered = data.job_cards;

  return (
    <>
      <Helmet>
        <style>{`body { background: ${BG}; }`}</style>
      </Helmet>

      <Box sx={{ px: "20px", pt: "80px", pb: "40px", maxWidth: "480px", mx: "auto" }}>
        {/* Page heading */}
        <Box sx={{ mb: "20px" }}>
          <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.15 }}>
            History
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)", mt: "5px" }}>
            Installs you approved or declined
          </Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by address, serial, IMSI…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: "20px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: CARD_BG,
              borderRadius: "10px",
              "& fieldset": { borderColor: BORDER },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
              "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.4)" },
            },
            "& .MuiInputBase-input": { color: "#fff", fontSize: "0.9rem" },
            "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
            "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.35)" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
          }}
        />

        {filtered.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", textAlign: "center", mt: "40px" }}>
            {search ? "No results" : "No history yet"}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {filtered.map((item) => (
              <Box
                key={item.id}
                onClick={() => navigate(`/QE/history/install/details/${item.id}/`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: CARD_BG,
                  border: `1px solid ${BORDER}`,
                  borderRadius: "12px",
                  px: "18px",
                  py: "16px",
                  cursor: "pointer",
                  transition: "background 0.15s",
                  "&:hover": { backgroundColor: "#1e2730" },
                }}
              >
                <Box sx={{ pr: "12px", minWidth: 0 }}>
                  <Typography sx={{ color: "#fff", fontSize: "0.95rem", fontWeight: 500 }}>
                    {item.address}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "4px" }}>
                    <JobTypeTag install_state={item.install_state} />
                    {jobSerial(item) && (
                      <Typography component="span" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", fontFamily: "monospace" }}>
                        {jobSerial(item)}
                      </Typography>
                    )}
                  </Box>
                  {/* Declined, then redone: the decline stays visible here (N22). */}
                  {item.declined_before && (
                    <Typography sx={{ color: "#f8a5a5", fontSize: "0.75rem", mt: "6px", lineHeight: 1.4 }}>
                      Declined before{item.declined_before_reason ? `: ${item.declined_before_reason}` : ""}
                    </Typography>
                  )}
                </Box>
                <StatusIcon status={item.qe_job_state} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

// Pending: a redo resubmitted after this QE declined it, waiting for review again.
function StatusIcon({ status }) {
  const label = status === "Approved" || status === "Pending" ? status : "Declined";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem", mb: "2px" }}>
        {label}
      </Typography>
      {label === "Approved" && <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "1.2rem" }} />}
      {label === "Pending" && <HourglassTopIcon sx={{ color: "#FDB940", fontSize: "1.2rem" }} />}
      {label === "Declined" && <CancelIcon sx={{ color: "#f44336", fontSize: "1.2rem" }} />}
    </Box>
  );
}
